import type { Faq as FaqItem } from 'libs/http/api/content/content.types';

import type { NumberedFeatureCard } from 'ui/organisms/NumberedFeatureCards/NumberedFeatureCards.types';
import type { PillarCardItem } from 'ui/organisms/PillarCards/PillarCards.types';
import type { DeliveryTableRow } from 'ui/organisms/DeliveryTable/DeliveryTable.types';
import type { NumberedRow } from 'ui/organisms/NumberedRowsSection/NumberedRowsSection.types';

type Translate = (key: string) => string;

/** Registry pill ticker (flag + name). Registry names are proper nouns — left untranslated. */
export type RegistryItem = { flag: string; name: string };

// ── Four ways to partner ────────────────────────────────────────────────────
export const getPartnerModels = (t: Translate): NumberedFeatureCard[] => [
  {
    num: '01',
    title: t('Data supply / OEM'),
    description: t(
      'Build our data into your product. Our fields blended into your own records and shipped under your brand, on a product-embedding licence.',
    ),
  },
  {
    num: '02',
    title: t('Redistribution'),
    description: t(
      'Deliver our records to your customers. Handed to them intact, on a redistribution licence with territory and named channels agreed.',
    ),
  },
  {
    num: '03',
    title: t('AI and agent platforms'),
    description: t(
      'Ground your agents in filed records. Your agents answer from our data at query time, on a retrieval grant. Training rights are agreed separately in writing.',
    ),
  },
  {
    num: '04',
    title: t('Referral and channel'),
    description: t(
      'Introduce the clients who need it. You introduce clients who need the data but will not license it themselves. Fee terms agreed in writing before the first introduction.',
    ),
  },
];

// ── Licence grants ──────────────────────────────────────────────────────────
export const getLicenceGrants = (t: Translate): NumberedFeatureCard[] => [
  { num: '01', title: t('Embed'), description: t('Inside your product, through your own interface.') },
  { num: '02', title: t('Redistribute'), description: t('Onward to your own clients, as part of what you sell.') },
  { num: '03', title: t('White-label'), description: t('Under your brand rather than ours.') },
  { num: '04', title: t('License on'), description: t('Through channel partners named in the agreement.') },
  { num: '05', title: t('Train AI'), description: t('Model training and grounding, licensed like everything else.') },
];

// ── Data set 01 — company data ──────────────────────────────────────────────
export const getCompanyDataBlocks = (t: Translate): NumberedFeatureCard[] => [
  {
    num: '01',
    title: t('Company registry data'),
    list: [
      t('Legal name and previous names'),
      t('Registration number, VAT and tax IDs'),
      t('Legal form and incorporation date'),
      t('Company status'),
      t('Registered and trading addresses'),
      t('Registry source and filing link'),
    ],
  },
  {
    num: '02',
    title: t('Financials'),
    list: [
      t('Filed revenue and turnover'),
      t('Assets, liabilities and net worth'),
      t('Profit and loss figures'),
      t('Filing history and latest accounts'),
      t('Currency and reporting period'),
      t('Multi-year financial trend'),
    ],
  },
  {
    num: '03',
    title: t('Firmographics'),
    list: [
      t('Industry codes: SIC, NACE, NAICS'),
      t('Employee counts and size bands'),
      t('Company age and trading status'),
      t('Websites and digital footprint'),
      t('Phone numbers'),
      t('Geographic and jurisdiction data'),
    ],
  },
  {
    num: '04',
    title: t('Shareholders and ownership'),
    list: [
      t('Shareholders and shareholdings'),
      t('Share classes and percentages'),
      t('Beneficial owners (PSCs and UBOs)'),
      t('Ownership percentages'),
      t('Ultimate beneficial owner chain'),
    ],
  },
  {
    num: '05',
    title: t('Group structure'),
    list: [
      t('Immediate parent'),
      t('Global ultimate owner'),
      t('Subsidiaries and branches'),
      t('Corporate linkage across registries'),
      t('Cross-registry entity resolution'),
    ],
  },
  {
    num: '06',
    title: t('Directors and officers'),
    list: [
      t('Directors and active officers'),
      t('Officer role and position'),
      t('Appointment and resignation dates'),
      t('Nationality and country of residence'),
      t('Date of birth, where filed'),
      t('Occupation'),
    ],
  },
  {
    num: '07',
    title: t('PEPs and sanctions'),
    list: [
      t('Screening across directors, officers and shareholders'),
      t('Every corporate role held by a flagged individual'),
      t('Declared roles cross-referenced against registry records'),
    ],
  },
  {
    num: '08',
    title: t('People linkage'),
    list: [
      t('Reverse officer lookup across all companies'),
      t('Reverse shareholder lookup by person or entity'),
      t('Ultimate beneficial owner discovery'),
      t('Ownership-network mapping'),
      t('A person’s full corporate footprint'),
    ],
  },
];

// ── Data set 02 — business contact data ─────────────────────────────────────
export const getContactDataBlocks = (t: Translate): PillarCardItem[] => [
  {
    title: t('Identity and role'),
    description: [t('Person name'), t('Job title'), t('Location'), t('LinkedIn profile')].join(' · '),
  },
  {
    title: t('Contact details'),
    description: [t('Work email'), t('Phone number')].join(' · '),
  },
  {
    title: t('Professional profile'),
    description: [t('Professional headline'), t('Work experience'), t('Education'), t('Skills')].join(' · '),
  },
];

// ── Government registries ticker (proper nouns — not translated) ─────────────
export const REGISTRIES: RegistryItem[] = [
  { flag: '🇬🇧', name: 'Companies House' },
  { flag: '🇩🇪', name: 'Handelsregister' },
  { flag: '🇫🇷', name: 'Registre National des Entreprises' },
  { flag: '🇳🇱', name: 'Kamer van Koophandel' },
  { flag: '🇮🇹', name: 'Registro delle Imprese' },
  { flag: '🇪🇸', name: 'Registro Mercantil' },
  { flag: '🇧🇪', name: 'Banque-Carrefour des Entreprises' },
  { flag: '🇵🇱', name: 'Krajowy Rejestr Sądowy' },
  { flag: '🇮🇪', name: 'Companies Registration Office' },
  { flag: '🇸🇪', name: 'Bolagsverket' },
  { flag: '🇩🇰', name: 'Erhvervsstyrelsen' },
  { flag: '🇳🇴', name: 'Brønnøysundregistrene' },
  { flag: '🇫🇮', name: 'Patentti- ja rekisterihallitus' },
  { flag: '🇦🇹', name: 'Firmenbuch' },
  { flag: '🇨🇭', name: 'Zefix' },
  { flag: '🇨🇿', name: 'Obchodní rejstřík' },
  { flag: '🇷🇴', name: 'Registrul Comerțului' },
  { flag: '🇱🇺', name: 'Registre de Commerce et des Sociétés' },
  { flag: '🇯🇵', name: 'National Tax Agency' },
  { flag: '🇺🇸', name: 'Secretaries of State' },
  { flag: '🇸🇬', name: 'ACRA' },
  { flag: '🇦🇺', name: 'ASIC' },
  { flag: '🇮🇳', name: 'Ministry of Corporate Affairs' },
  { flag: '🇭🇰', name: 'Companies Registry' },
  { flag: '🇲🇽', name: 'Registro Publico de Comercio' },
  { flag: '🇦🇪', name: 'Department of Economic Development' },
];

// ── Delivery ────────────────────────────────────────────────────────────────
export const getDeliveryRows = (t: Translate): DeliveryTableRow[] => [
  {
    name: t('API'),
    desc: t('Live lookups inside your product — verification, officers, shareholders, financials, group structure.'),
    actions: [{ label: t('API documentation'), href: 'https://api.globaldatabase.com/docs/v2/#setup' }],
  },
  {
    name: t('Regis API'),
    desc: t(
      'Regis is our AI, trained only on official registry filings. Your users ask a question in plain language; Regis finds the right records and returns a sourced answer — so nobody has to know which endpoint or field to call.',
    ),
    actions: [{ label: t('See Regis'), href: 'https://www.globaldatabase.com/regis' }],
  },
  {
    name: t('Contact enrichment'),
    desc: t(
      'Resolve a person by name, company domain or LinkedIn profile; returns a verified work email and business phone.',
    ),
    actions: [{ label: t('Enrichment endpoints'), href: 'https://api.globaldatabase.com/docs/v2/#setup' }],
  },
  {
    name: t('Bulk feed and deltas'),
    desc: t(
      'Full corpus or a scoped subset into storage you own, then only the changed records on the same schema and keys.',
    ),
    actions: [{ label: t('Master data bulk feed'), href: 'https://www.globaldatabase.com/master-data-bulk-feed' }],
  },
  {
    name: t('MCP server'),
    desc: t(
      'The licensed corpus exposed as tools to Claude and any other MCP client, on the same key and provenance fields as the API.',
    ),
    actions: [{ label: t('MCP integration'), href: 'https://www.globaldatabase.com/mcp-integration' }],
  },
  {
    name: t('CRM connectors'),
    desc: t('Enrich, verify and refresh company records inside the CRM your customers already work in.'),
    actions: [
      {
        label: 'HubSpot',
        href: 'https://ecosystem.hubspot.com/marketplace/listing/global-database-intelligence-enrichment',
      },
      {
        label: 'Salesforce',
        href: 'https://appexchange.salesforce.com/appxListingDetail?listingId=7d8cb601-add2-47f7-a345-4008cbb0cfe4',
      },
    ],
  },
  {
    name: t('Assistant connectors'),
    desc: t(
      'Registry data inside Claude and ChatGPT, under your own access controls. Ask for a company in plain language, get the sourced record back.',
    ),
    actions: [
      { label: 'Claude', href: 'https://claude.ai/new#directory/connectors/global-database' },
      { label: 'ChatGPT', href: 'https://chatgpt.com/plugins/plugin_asdk_app_696f807d21a481918a1ed1f43d719ce9' },
    ],
  },
];

// ── Pipeline ────────────────────────────────────────────────────────────────
export const getPipelineRows = (t: Translate): NumberedRow[] => [
  {
    index: '01',
    title: t('Registry connections'),
    body: t(
      '400+ direct connections, maintained by us and synced on each registry’s own schedule, daily in most major markets.',
    ),
  },
  {
    index: '02',
    title: t('Entity resolution'),
    body: t(
      'Filings are matched, deduplicated and resolved before they reach you, so records drop into a warehouse or CRM with no cleanup layer.',
    ),
  },
  {
    index: '03',
    title: t('Field-level provenance'),
    body: t(
      'Every field carries its source registry, filing date and last-verified timestamp, and all three export with the data.',
    ),
  },
  {
    index: '04',
    title: t('Digitised financials'),
    body: t(
      'Filed accounts turned into comparable line items, with the original document kept alongside the structured record.',
    ),
  },
  {
    index: '05',
    title: t('Ownership resolution'),
    body: t(
      'Traced through the chain to the ultimate owner, across borders, from the filed disclosures rather than an estimate.',
    ),
  },
];

// ── FAQ ─────────────────────────────────────────────────────────────────────
export const getFaqItems = (t: Translate): FaqItem[] => [
  {
    id: 'faq-1',
    question: t('Who is the partner programme for?'),
    answer: t(
      'Three kinds of company: teams that build our data into their own product, platforms that deliver our records to their customers, and AI or agent products that need company facts grounded in filed records at query time. Referral and channel arrangements are handled case by case. If you sit in more than one group, we scope one agreement that covers both.',
    ),
    default_opened: true,
  },
  {
    id: 'faq-2',
    question: t('Can our customers see, export and keep the data?'),
    answer: t(
      'For registry-sourced company data, yes: redistribution to your end customers sits in the base licence rather than behind a surcharge, the territory is worldwide, and records delivered during the term remain yours after it ends — what stops is the flow of new records and updates. Business contact data reaches you through our contact data partners, so onward distribution rights are confirmed in writing during scoping rather than assumed.',
    ),
  },
  {
    id: 'faq-3',
    question: t('Where does the data come from?'),
    answer: t(
      'Directly from official government company registries, gazettes and filing authorities across 200+ countries. Each field names the registry it came from and carries the filing date and our retrieval date, and that provenance travels into your product.',
    ),
  },
  {
    id: 'faq-4',
    question: t('How do we connect technically?'),
    answer: t(
      'REST API for live lookups, bulk files (Parquet, CSV, JSONL) and daily deltas for your own warehouse, and an MCP server for AI agents. We also publish connectors in the Salesforce, HubSpot, Microsoft Dynamics, Claude and ChatGPT marketplaces. We map fields to your schema before the first delivery.',
    ),
  },
  {
    id: 'faq-5',
    question: t('Is there a fee to join?'),
    answer: t(
      'No. Applying is free. Commercial terms depend on the model you choose: embedded and integration partners license the data, referral partners agree fee terms in a short written agreement before the first introduction.',
    ),
  },
  {
    id: 'faq-6',
    question: t('Is the contact data sourced from registries too?'),
    answer: t(
      'No, and we label the difference rather than blur it. Company records are first-party from official government registries, and each field names the registry it came from. Business contact records are matched and verified through a separate process, and the API returns them under their own source category. Both sit in one agreement, but a compliance reviewer can always tell which is which.',
    ),
  },
  {
    id: 'faq-7',
    question: t('What kind of contact data is it, and is it compliant?'),
    answer: t(
      'Business contacts only: person name, job title, work email, phone number, LinkedIn profile, location, professional headline, work experience, education and skills. Each lookup runs a waterfall across 15 contact data partners in sequence until a verified match comes back, which is what holds match rates up at volume. No consumer records. Global Data Intelligence Limited is registered with the UK Information Commissioner’s Office, and every delivery runs under a signed data processing agreement.',
    ),
  },
  {
    id: 'faq-8',
    question: t('How long does it take to get started?'),
    answer: t(
      'You hear back from a named person on the partnerships team within two business days. Most integration partners receive a scoped sample in their own schema before anything is signed, so your engineers test the real data rather than a marketing extract.',
    ),
  },
];
